import React from "react"
import { Collapsible } from "@base-ui/react/collapsible"
import Button from "../button"
import { Icon } from "../icon"
import VisuallyHidden from "../visually-hidden"
import { ProgressCircle } from "../progress-circle"

// =============================================================================
// Props
// =============================================================================

export type TrialIndicatorProps = {
  /** Days remaining in the trial */
  daysRemaining: number
  /** Total days in the trial period */
  daysTotal: number
  /** Called when "Upgrade now" is clicked */
  onUpgrade: () => void
  /** Whether the card starts open. Defaults to true. */
  defaultOpen?: boolean
}

// =============================================================================
// Styles
// =============================================================================

const cardStyles = [
  "flex flex-col rounded-lg p-2",
  "bg-blue-100 text-gray-900",
].join(" ")

const triggerStyles = [
  // 'group' enables group-aria-[expanded] variants on children
  "group flex w-full items-center gap-2 p-2",
  "rounded-lg",
].join(" ")

const panelStyles = [
  "h-[var(--collapsible-panel-height)] overflow-hidden",
  "transition-[height,opacity] ease-out data-[ending-style]:h-0 data-[starting-style]:h-0 duration-150",
].join(" ")

const collapsedSummaryStyles = [
  "text-sm font-semibold leading-6",
  "transition-opacity duration-150 ease-out",
  "group-aria-[expanded=true]:opacity-0 group-aria-[expanded=true]:pointer-events-none",
].join(" ")

// =============================================================================
// Component
// =============================================================================

/**
 * Collapsible card in the navigation sidebar showing trial days remaining
 * and an upgrade CTA. Click the header row to expand or collapse.
 */
export function TrialIndicator({
  daysRemaining,
  daysTotal,
  onUpgrade,
  defaultOpen = true,
}: TrialIndicatorProps) {
  const safeDaysRemaining = Math.max(0, daysRemaining)
  const progress = daysTotal > 0 ? Math.min(safeDaysRemaining / daysTotal, 1) : 0
  const dayLabel = safeDaysRemaining === 1 ? "day" : "days"

  return (
    <div data-slot="trial-indicator" className={cardStyles}>
      <Collapsible.Root defaultOpen={defaultOpen}>
        <Collapsible.Trigger type="button" className={triggerStyles}>
          <span className="text-blue-800 flex shrink-0">
            <ProgressCircle progress={progress} />
          </span>
          <div className="flex-1 text-left">
            <span aria-hidden className={collapsedSummaryStyles}>
              {safeDaysRemaining} / {daysTotal} {dayLabel}
            </span>
            <VisuallyHidden>{safeDaysRemaining} / {daysTotal} {dayLabel} remaining in trial</VisuallyHidden>
          </div>
          <Icon
            name="down"
            className="shrink-0 transition-transform group-aria-[expanded=true]:-rotate-180"
            aria-hidden
          />
        </Collapsible.Trigger>
        <Collapsible.Panel className={panelStyles}>
          <div className="flex flex-col gap-4 p-2 items-start">
            <div className="w-full flex flex-col gap-1 text-sm leading-6">
              <p className="font-semibold">
                {safeDaysRemaining} {dayLabel} left on your free trial
              </p>
              <p>
                Upgrade to continue to use all the features you have access to
                without interruption.
              </p>
            </div>
            <Button is="primary-blue" size="sm" onClick={onUpgrade}>
              Upgrade now
            </Button>
          </div>
        </Collapsible.Panel>
      </Collapsible.Root>
    </div>
  )
}

TrialIndicator.displayName = "TrialIndicator"
